import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { CommonModule, DecimalPipe } from '@angular/common';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { PreloaderComponent } from './components/layouts/preloader/preloader.component';
import { FooterComponent } from './components/layouts/footer/footer.component';
import { HomeOneComponent } from './components/pages/home-one/home-one.component';
import { NavbarComponent } from './components/layouts/navbar/navbar.component';
import { HomeTwoComponent } from './components/pages/home-two/home-two.component';
import { HomeThreeComponent } from './components/pages/home-three/home-three.component';
import { HomeFourComponent } from './components/pages/home-four/home-four.component';
import { HomeFiveComponent } from './components/pages/home-five/home-five.component';
import { AboutComponent } from './components/pages/about/about.component';
import { ServicesComponent } from './components/pages/services/services.component';
import { AffiliateComponent } from './components/pages/affiliate/affiliate.component';
import { TeamComponent } from './components/pages/team/team.component';
import { FaqComponent } from './components/pages/faq/faq.component';
import { LogInComponent } from './components/pages/log-in/log-in.component';
import { SignUpComponent } from './components/pages/sign-up/sign-up.component';
import { RecoverPasswordComponent } from './components/pages/recover-password/recover-password.component';
import { TermsConditionsComponent } from './components/pages/terms-conditions/terms-conditions.component';
import { PrivacyPolicyComponent } from './components/pages/privacy-policy/privacy-policy.component';
import { ContactComponent } from './components/pages/contact/contact.component';

import { NewsGridComponent } from './components/pages/news-grid/news-grid.component';
import { NewsLeftSidebarComponent } from './components/pages/news-left-sidebar/news-left-sidebar.component';
import { NewsRightSidebarComponent } from './components/pages/news-right-sidebar/news-right-sidebar.component';
import { NewsDetailsComponent } from './components/pages/news-details/news-details.component';
import { DomainComponent } from './components/pages/domain/domain.component';
import { SharedHostingComponent } from './components/pages/shared-hosting/shared-hosting.component';
import { ResellerHostingComponent } from './components/pages/reseller-hosting/reseller-hosting.component';
import { DedicatedHostingComponent } from './components/pages/dedicated-hosting/dedicated-hosting.component';
import { ComingSoonComponent } from './components/pages/coming-soon/coming-soon.component';
import { ErrorComponent } from './components/pages/error/error.component';
import { WindowsCloudComponent } from './components/pages/windows-cloud/windows-cloud.component';
import { GpuCloudComponent } from './components/pages/gpu-cloud/gpu-cloud.component';
import { ManagedItComponent } from './components/pages/managed-it/managed-it.component';
import { CloudMigrationComponent } from './components/pages/cloud-migration/cloud-migration.component';
import { CybersecurityComponent } from './components/pages/cybersecurity/cybersecurity.component';
import { BackupRecoveryComponent } from './components/pages/backup-recovery/backup-recovery.component';
import { CareersComponent } from './components/pages/careers/careers.component';
import { LinuxCloudComponent } from './components/pages/linux-cloud/linux-cloud.component';
import { StorageCloudComponent } from './components/pages/storage-cloud/storage-cloud.component';
import { VirtualPrivateCloudComponent } from './components/pages/virtual-private-cloud/virtual-private-cloud.component';
import { LoadBalancersComponent } from './components/pages/load-balancers/load-balancers.component';
import { LoadBalancersProfessionalComponent } from './components/pages/load-balancers/load-balancers-professional.component';
import { CloudInfrastructureComponent } from './components/pages/cloud-infrastructure/cloud-infrastructure.component';
import { DocumentationComponent } from './components/pages/documentation/documentation.component';
import { GettingStartedComponent } from './components/pages/documentation/getting-started/getting-started.component';
import { MyAccountComponent } from './components/pages/my-account/my-account.component';
import { CaseStudiesComponent } from './components/pages/case-studies/case-studies.component';
import { HealthcareDiagnosticsComponent } from './components/pages/case-study-details/healthcare-diagnostics/healthcare-diagnostics.component';
import { FinanceFraudComponent } from './components/pages/case-study-details/finance-fraud/finance-fraud.component';
import { ProxmoxApiExplorerComponent } from './components/pages/proxmox-api-explorer/proxmox-api-explorer.component';
import { DataCentersComponent } from './components/pages/data-centers/data-centers.component';
import { DbaasComponent } from './components/pages/dbaas/dbaas.component';
import { DevopsComponent } from './components/pages/devops/devops.component';

// Import new CMP components
import { AdminDashboardComponent } from './components/pages/admin-dashboard/admin-dashboard.component';
import { ClientDashboardComponent } from './components/pages/client-dashboard/client-dashboard.component';
import { VmDetailsComponent } from './components/pages/vm-details/vm-details.component';
import { ClientLoginComponent } from './components/pages/client-login/client-login.component';
import { ClientNavbarComponent } from './components/layouts/client-navbar/client-navbar.component';
import { ClientLayoutComponent } from './components/layouts/client-layout/client-layout.component';
import { ClientRegistrationComponent } from './components/pages/client-registration/client-registration.component';

// Import newsletter component
import { NewsletterComponent } from './components/shared/newsletter/newsletter.component';

// Import new storage components
import { IisVolumeComponent } from './components/pages/iis-volume/iis-volume.component';
import { IisCdpBackupComponent } from './components/pages/iis-cdp-backup/iis-cdp-backup.component';
import { SavedImagesComponent } from './components/pages/saved-images/saved-images.component';
import { WindowsSqlCloudComponent } from './components/pages/windows-sql-cloud/windows-sql-cloud.component';

// Import Pricing Module
import { PricingModule } from './components/pages/pricing/pricing.module';

// Import Pricing Calculator Module
import { PricingCalculatorModule } from './components/pages/pricing-calculator/pricing-calculator.module';

// Import new Linux series components
import { LinuxC3SeriesComponent } from './components/pages/linux-c3-series/linux-c3-series.component';
import { LinuxM3SeriesComponent } from './components/pages/linux-m3-series/linux-m3-series.component';
import { LinuxSdc3SeriesComponent } from './components/pages/linux-sdc3-series/linux-sdc3-series.component';
import { LinuxE1SeriesComponent } from './components/pages/linux-e1-series/linux-e1-series.component';

// Import Event component
import { EventComponent } from './components/pages/event/event.component';
// Import Partner component
import { PartnerComponent } from './components/pages/partner/partner.component';

// Import new AI-themed home component
import { HomeAIComponent } from './components/pages/home-ai/home-ai.component';

// Import visitor counter component
import { VisitorCounterComponent } from './components/shared/visitor-counter/visitor-counter.component';

// Import cookie consent component
import { CookieConsentComponent } from './components/shared/cookie-consent/cookie-consent.component';

// Import pricing calculator component
import { PricingCalculatorComponent } from './components/pages/pricing-calculator/pricing-calculator.component';

// Import URL redirect component
import { UrlRedirectComponent } from './components/pages/url-redirect/url-redirect.component';

// Import services
import { AuthGuardService } from './services/auth-guard.service';
import { ClientManagementService } from './services/client-management.service';
import { VmManagementService } from './services/vm-management.service';
import { ProxmoxService } from './services/proxmox.service';
import { MailchimpService } from './services/mailchimp.service';
import { NewsletterPopupService } from './services/newsletter-popup.service';
import { SeoService } from './services/seo.service';

@NgModule({
  declarations: [
    AppComponent,
    PreloaderComponent,
    FooterComponent,
    HomeOneComponent,
    NavbarComponent,
    HomeTwoComponent,
    HomeThreeComponent,
    HomeFourComponent,
    HomeFiveComponent,
    AboutComponent,
    DataCentersComponent,
    ServicesComponent,
    AffiliateComponent,
    TeamComponent,
    FaqComponent,
    LogInComponent,
    SignUpComponent,
    RecoverPasswordComponent,
    TermsConditionsComponent,
    PrivacyPolicyComponent,
    ContactComponent,
    NewsGridComponent,
    NewsLeftSidebarComponent,
    NewsRightSidebarComponent,
    NewsDetailsComponent,
    DomainComponent,
    SharedHostingComponent,
    ResellerHostingComponent,
    DedicatedHostingComponent,
    ComingSoonComponent,
    ErrorComponent,
    WindowsCloudComponent,
    GpuCloudComponent,
    ManagedItComponent,
    CloudMigrationComponent,
    CybersecurityComponent,
    BackupRecoveryComponent,
    CareersComponent,
    LinuxCloudComponent,
    StorageCloudComponent,
    VirtualPrivateCloudComponent,
    LoadBalancersComponent,
    LoadBalancersProfessionalComponent,
    CloudInfrastructureComponent,
    DocumentationComponent,
    GettingStartedComponent,
    MyAccountComponent,
    CaseStudiesComponent,
    HealthcareDiagnosticsComponent,
    FinanceFraudComponent,
    ProxmoxApiExplorerComponent,
    DbaasComponent,
    DevopsComponent,
    // Declare new CMP components
    AdminDashboardComponent,
    ClientDashboardComponent,
    VmDetailsComponent,
    ClientLoginComponent,
    ClientNavbarComponent,
    ClientLayoutComponent,
    ClientRegistrationComponent,
    // Declare newsletter component
    NewsletterComponent,
    // Declare new storage components
    IisVolumeComponent,
    IisCdpBackupComponent,
    SavedImagesComponent,
    // Declare new Windows SQL Cloud component
    WindowsSqlCloudComponent,
    // Declare new Linux series components
    LinuxC3SeriesComponent,
    LinuxM3SeriesComponent,
    LinuxSdc3SeriesComponent,
    LinuxE1SeriesComponent,
    // Declare Event component
    EventComponent,
    // Declare Partner component
    PartnerComponent,
    // Declare new AI-themed home component
    HomeAIComponent,
    // Declare visitor counter component
    VisitorCounterComponent,
    // Declare cookie consent component
    CookieConsentComponent,
    // Declare URL redirect component
    UrlRedirectComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    HttpClientModule,
    CommonModule,
    PricingModule,
    PricingCalculatorModule
  ],
  providers: [
    // Add services to providers
    AuthGuardService,
    ClientManagementService,
    VmManagementService,
    ProxmoxService,
    MailchimpService,
    NewsletterPopupService,
    SeoService,
    DecimalPipe
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
